import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "ashren_cookie_consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = !!localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      console.warn("CookieBanner: localStorage unavailable", err);
    }
    if (!dismissed) {
      // Small delay so it doesn't fight the entrance animations
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const persist = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ value, at: Date.now() }));
    } catch (err) {
      console.warn("CookieBanner: failed to persist consent", err);
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[70]"
      data-testid="cookie-banner"
    >
      <div className="bg-[color:var(--primary-dark)] text-white rounded-2xl shadow-2xl p-5 md:p-6 relative border border-white/10">
        <button
          type="button"
          onClick={() => persist("dismissed")}
          className="absolute top-3 right-3 text-white/50 hover:text-white transition-colors"
          aria-label="Dismiss"
          data-testid="cookie-banner-close"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div
            className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center"
            style={{ background: "rgba(46,204,155,0.15)", color: "var(--accent-mint)" }}
          >
            <Cookie size={18} />
          </div>
          <div>
            <div className="font-display text-lg leading-tight">Cookies on this site</div>
            <p className="text-sm text-white/70 mt-1 leading-relaxed">
              We use essential cookies to run the site and a small amount of analytics to improve
              it. No marketing trackers, ever. See our{" "}
              <Link to="/cookies" className="underline underline-offset-2 hover:text-white" data-testid="cookie-banner-policy-link">
                Cookie Policy
              </Link>{" "}
              for detail.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pl-12">
          <button
            type="button"
            onClick={() => persist("accepted")}
            className="btn-primary bg-white text-[color:var(--primary-dark)] border-white hover:bg-[color:var(--accent-mint)] hover:border-[color:var(--accent-mint)] hover:text-[color:var(--primary-dark)]"
            data-testid="cookie-banner-accept"
          >
            Accept
          </button>
          <button
            type="button"
            onClick={() => persist("essential_only")}
            className="btn-secondary border-white/30 text-white hover:bg-white/10 hover:text-white"
            data-testid="cookie-banner-essential"
          >
            Essential only
          </button>
        </div>
      </div>
    </div>
  );
}
